/**
 * Tipos e derivação pura da matriz de stats da inbox.
 * Sem dependência do cliente Supabase — importável em testes unitários.
 *
 * Contrato espelha `get_conversation_inbox_filter_counts` (V1):
 * - `open`/`closed`/`listTotal`: conjunto visível, sem recorte de status
 * - `all`/`mine`/`attention`/`ia`/`unread`: recorte do `statusFilter` (attention scope)
 */
import type {
  ConversationFilterCounts,
  ConversationStatusFilter,
} from "@/lib/conversation-filters";

export type InboxChannel = "whatsapp" | "instagram" | "messenger";

/** Linha da matriz por (canal, integração), como vem de `get_inbox_stats_v2`. */
export type InboxStatsRow = {
  channel: InboxChannel;
  /** `null` = conversas sem integração vinculada. */
  integrationId: string | null;
  open: number;
  closed: number;
  unreadOpen: number;
  unreadClosed: number;
  mine: number;
  attention: number;
  ia: number;
  mineClosed: number;
  attentionClosed: number;
  iaClosed: number;
};

export type InboxTabCounts = Pick<
  ConversationFilterCounts,
  "mine" | "attention" | "ia" | "all" | "open" | "closed"
> & {
  unread?: number;
  listTotal: number;
};

export interface InboxTabCountsParams {
  workspaceId: string;
  channel: InboxChannel;
  integrationId?: string | null;
  statusFilter: ConversationStatusFilter;
  memberId?: string | null;
}

export interface InboxMatrixScope {
  channel: InboxChannel;
  /** UUID da integração, ou `null` para "todas as conexões do canal". */
  integrationId?: string | null;
  /** Filtro "não atribuídas": só linhas sem integração. */
  filterUnassigned?: boolean;
  /** `null` = aba "Todas" (sem recorte por status). */
  statusFilter: ConversationStatusFilter | null;
}

const EMPTY: InboxTabCounts = {
  mine: 0,
  attention: 0,
  ia: 0,
  all: 0,
  open: 0,
  closed: 0,
  unread: 0,
  listTotal: 0,
};

/** Soma as linhas do recorte atual da matriz V2 no contrato das abas. */
export function deriveTabCountsFromMatrix(
  rows: InboxStatsRow[],
  scope: InboxMatrixScope,
): InboxTabCounts {
  const matching = rows.filter((row) => {
    if (row.channel !== scope.channel) return false;
    if (scope.filterUnassigned) return row.integrationId === null;
    if (!scope.integrationId) return true;
    return row.integrationId === scope.integrationId;
  });

  if (matching.length === 0) return { ...EMPTY };

  const sum = matching.reduce(
    (acc, row) => ({
      open: acc.open + row.open,
      closed: acc.closed + row.closed,
      unreadOpen: acc.unreadOpen + row.unreadOpen,
      unreadClosed: acc.unreadClosed + row.unreadClosed,
      mine: acc.mine + row.mine,
      attention: acc.attention + row.attention,
      ia: acc.ia + row.ia,
      mineClosed: acc.mineClosed + row.mineClosed,
      attentionClosed: acc.attentionClosed + row.attentionClosed,
      iaClosed: acc.iaClosed + row.iaClosed,
    }),
    {
      open: 0,
      closed: 0,
      unreadOpen: 0,
      unreadClosed: 0,
      mine: 0,
      attention: 0,
      ia: 0,
      mineClosed: 0,
      attentionClosed: 0,
      iaClosed: 0,
    },
  );

  const scoped = <T>(open: T, closed: T, both: T): T =>
    scope.statusFilter === "open" ? open : scope.statusFilter === "closed" ? closed : both;

  return {
    open: sum.open,
    closed: sum.closed,
    listTotal: sum.open + sum.closed,
    all: scoped(sum.open, sum.closed, sum.open + sum.closed),
    unread: scoped(sum.unreadOpen, sum.unreadClosed, sum.unreadOpen + sum.unreadClosed),
    mine: scoped(sum.mine, sum.mineClosed, sum.mine + sum.mineClosed),
    attention: scoped(sum.attention, sum.attentionClosed, sum.attention + sum.attentionClosed),
    ia: scoped(sum.ia, sum.iaClosed, sum.ia + sum.iaClosed),
  };
}

/** Total global de não lidas do membro (todos os canais e conexões). */
export function deriveUnreadTotalFromMatrix(rows: InboxStatsRow[]): number {
  return rows.reduce((acc, row) => acc + row.unreadOpen + row.unreadClosed, 0);
}

/** Matriz vazia pode ser inbox zerada ou backfill ausente — só confiamos com linhas. */
export function hasUsableMatrixRows(rows: InboxStatsRow[] | null): rows is InboxStatsRow[] {
  return Array.isArray(rows) && rows.length > 0;
}

/** Conversas visíveis do canal com integration_id IS NULL (abertas + fechadas). */
export function deriveUnassignedCount(rows: InboxStatsRow[], channel: InboxChannel): number {
  return deriveTabCountsFromMatrix(rows, {
    channel,
    filterUnassigned: true,
    statusFilter: null,
  }).listTotal;
}

/** Conversas visíveis da integração no canal (abertas + fechadas). */
export function deriveCountForIntegration(
  rows: InboxStatsRow[],
  channel: InboxChannel,
  integrationId: string,
): number {
  return deriveTabCountsFromMatrix(rows, {
    channel,
    integrationId,
    statusFilter: null,
  }).listTotal;
}

export type ChannelCountsRemoteMode = "legacy" | "matrix-canary" | "matrix";
export type ChannelCountsEffective = { mode: "legacy" | "matrix"; shadow: boolean };

/** Espelha get_channel_counts_config — testável sem banco (harness F0). */
export function deriveChannelCountsEffectiveConfig(params: {
  remoteMode: ChannelCountsRemoteMode;
  workspaceId: string;
  canaryWorkspaceIds: string[];
  isWorkspaceMember: boolean;
}): ChannelCountsEffective {
  if (!params.isWorkspaceMember) return { mode: "legacy", shadow: false };
  if (params.remoteMode === "legacy") return { mode: "legacy", shadow: false };
  if (params.remoteMode === "matrix") return { mode: "matrix", shadow: false };
  if (params.canaryWorkspaceIds.includes(params.workspaceId)) {
    return { mode: "matrix", shadow: true };
  }
  return { mode: "legacy", shadow: false };
}

export type ChannelCountsParityVector = {
  unassigned: Record<InboxChannel, number>;
  integrations: Array<{ channel: InboxChannel; integrationId: string; count: number }>;
};

export function deriveChannelCountsParityVector(
  rows: InboxStatsRow[],
  integrationIds: Array<{ channel: InboxChannel; integrationId: string }>,
): ChannelCountsParityVector {
  return {
    unassigned: {
      whatsapp: deriveUnassignedCount(rows, "whatsapp"),
      instagram: deriveUnassignedCount(rows, "instagram"),
      messenger: deriveUnassignedCount(rows, "messenger"),
    },
    integrations: integrationIds.map(({ channel, integrationId }) => ({
      channel,
      integrationId,
      count: deriveCountForIntegration(rows, channel, integrationId),
    })),
  };
}

export function hashChannelCountsParityVector(vector: ChannelCountsParityVector): string {
  const parts = [
    `u:wa:${vector.unassigned.whatsapp}`,
    `u:ig:${vector.unassigned.instagram}`,
    `u:ms:${vector.unassigned.messenger}`,
    ...[...vector.integrations]
      .sort((a, b) => `${a.channel}:${a.integrationId}`.localeCompare(`${b.channel}:${b.integrationId}`))
      .map((row) => `i:${row.channel}:${row.integrationId}:${row.count}`),
  ];
  const s = parts.join("|");
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
  return (h >>> 0).toString(16);
}
